/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import store from 'store';
import {IAppStore} from 'store/reducers';

import {getChannelMessages} from 'services/channel.service';
import {getRoomMessages} from 'services/room.service';

import {IGetMessagesResponse, IMessage, IMessagesParams} from 'interfaces';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

/**
 * ACTION TYPES
 */
export const GET_MESSAGES_REQUEST = 'messages::GET_MESSAGES_REQUEST' as const;
export const GET_MESSAGES_DONE = 'messages::GET_MESSAGES_DONE' as const;
export const GET_MESSAGES_FAILED = 'messages::GET_MESSAGES_FAILED' as const;

/**
 * SELECTORS
 */
export const messagesStateSelector = ({messages}: IAppStore): IAppStore['messages'] => messages;
export const messagesIsFetchingSelector = ({messages}: IAppStore): boolean => messages.isFetching;
export const messagesErrorSelector = ({messages}: IAppStore): IPortalError => messages.error;
export const messagesListSelector = ({messages}: IAppStore, view: string): IMessage[] => messages[view].messages;
export const messagesHasMoreBeforeSelector = ({messages}: IAppStore, view: string): boolean => messages[view].hasMoreBefore;

/**
 * INTERFACES
 */
interface IRequestMessages {
  type: typeof GET_MESSAGES_REQUEST;
  payload: {id: string; params: IMessagesParams};
}
interface IReceiveMessages {
  type: typeof GET_MESSAGES_DONE;
  payload: IGetMessagesResponse;
}
interface IFailedMessages {
  type: typeof GET_MESSAGES_FAILED;
  error: null | IPortalError;
}
interface IMessagesActions {
  request: (payload: {id: string; params: IMessagesParams}) => MessagesActionsType;
  receive: (payload: IGetMessagesResponse) => MessagesActionsType;
  failed: (error: null | IPortalError) => MessagesActionsType;
}
export type MessagesActionsType = IRequestMessages | IReceiveMessages | IFailedMessages;

/**
 * ACTIONS
 */
export const messagesActions: IMessagesActions = {
  request: payload => ({
    type: GET_MESSAGES_REQUEST,
    payload
  }),
  receive: payload => ({
    type: GET_MESSAGES_DONE,
    payload
  }),
  failed: error => ({
    type: GET_MESSAGES_FAILED,
    error
  })
};

export const getMessages = (
  id: string,
  view: string,
  params?: IMessagesParams,
  callback?: () => void
) => async(dispatch: Dispatch<MessagesActionsType>): Promise<void> => {
  const {receive, request, failed} = messagesActions;

  dispatch(request({
    id,
    params
  }));

  try {
    const requestByView = {
      channel: getChannelMessages,
      room: getRoomMessages
    };
    const response = await requestByView[view](id, params);

    if (response) {
      const {messages} = store.getState();
      const loadMore = !!(id === messages[view].id && params?.beforeMessageId);
      const messagesList = loadMore ? [...response.messages, ...messages[view].messages] : response.messages;

      dispatch(receive({
        ...response,
        messages: messagesList,
        view,
        id
      }));
    }

    if (callback) {
      callback();
    }
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};